import { Users, DollarSign, TrendingUp, Dumbbell, UserCheck, BarChart3, UserPlus, AlertCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { ExtendedGymKPIs } from "@/hooks/gym-stats/useGymExtendedKPIs";
import { MetricConfig } from "@/hooks/gym-stats/useGymMetricsConfig";
import { DroppableArea } from "./DraggableMetricsGrid";
import { DraggableKPICard } from "./DraggableKPICard";

interface KPICardProps {
  title: string;
  value: string;
  change?: number;
  icon: React.ReactNode;
  iconBg: string;
}

const KPICard = ({ title, value, change, icon, iconBg }: KPICardProps) => {
  const isPositive = change && change >= 0;
  
  return (
    <Card className="border-border/50 bg-card/50 backdrop-blur-sm hover:bg-card/80 transition-colors h-full">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-3xl font-bold text-foreground">{value}</p>
            {change !== undefined && (
              <div className="flex items-center gap-1">
                <TrendingUp 
                  className={cn(
                    "h-4 w-4",
                    isPositive ? "text-green-500" : "text-red-500 rotate-180"
                  )} 
                />
                <span className={cn(
                  "text-sm font-medium",
                  isPositive ? "text-green-500" : "text-red-500"
                )}>
                  {isPositive ? "+" : ""}{change.toFixed(1)}%
                </span>
              </div>
            )}
          </div>
          <div className={cn("p-3 rounded-xl", iconBg)}>
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

interface DynamicKPICardsProps {
  kpis: ExtendedGymKPIs;
  enabledMetrics: MetricConfig[];
  isDraggable?: boolean;
  isEditMode?: boolean;
}

export const DynamicKPICards = ({ kpis, enabledMetrics, isDraggable = true, isEditMode = false }: DynamicKPICardsProps) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getKPIConfig = (id: string) => {
    const configs: Record<string, { title: string; getValue: () => string; getChange?: () => number; icon: React.ReactNode; iconBg: string }> = {
      totalUsers: {
        title: "Total Usuarios",
        getValue: () => kpis.totalUsers.toString(),
        icon: <Users className="h-6 w-6 text-blue-500" />,
        iconBg: "bg-blue-500/10",
      },
      monthlyRevenue: {
        title: "Ingresos del Mes",
        getValue: () => formatCurrency(kpis.monthlyRevenue),
        getChange: () => kpis.revenueChange,
        icon: <DollarSign className="h-6 w-6 text-green-500" />,
        iconBg: "bg-green-500/10",
      },
      attendanceRate: {
        title: "Tasa de Asistencia",
        getValue: () => `${kpis.attendanceRate.toFixed(0)}%`,
        icon: <TrendingUp className="h-6 w-6 text-purple-500" />,
        iconBg: "bg-purple-500/10",
      },
      completedRoutines: {
        title: "Rutinas Completadas",
        getValue: () => kpis.completedRoutines.toString(),
        getChange: () => kpis.routinesChange,
        icon: <Dumbbell className="h-6 w-6 text-orange-500" />,
        iconBg: "bg-orange-500/10",
      },
      retentionRate: {
        title: "Tasa de Retención",
        getValue: () => `${kpis.retentionRate.toFixed(0)}%`,
        icon: <UserCheck className="h-6 w-6 text-teal-500" />,
        iconBg: "bg-teal-500/10",
      },
      averageTicket: {
        title: "Ticket Promedio",
        getValue: () => formatCurrency(kpis.averageTicket),
        icon: <BarChart3 className="h-6 w-6 text-indigo-500" />,
        iconBg: "bg-indigo-500/10",
      },
      newUsersMonth: {
        title: "Usuarios Nuevos",
        getValue: () => kpis.newUsersMonth.toString(),
        icon: <UserPlus className="h-6 w-6 text-cyan-500" />,
        iconBg: "bg-cyan-500/10",
      },
      penalizedUsers: {
        title: "Penalizaciones",
        getValue: () => kpis.penalizedUsers.toString(),
        icon: <AlertCircle className="h-6 w-6 text-red-500" />,
        iconBg: "bg-red-500/10",
      },
    };
    return configs[id];
  };

  if (enabledMetrics.length === 0) return null;

  if (!isDraggable) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {enabledMetrics.map(metric => {
          const config = getKPIConfig(metric.id);
          if (!config) return null;
          return (
            <KPICard 
              key={metric.id}
              title={config.title}
              value={config.getValue()}
              change={config.getChange?.()}
              icon={config.icon}
              iconBg={config.iconBg}
            />
          );
        })}
      </div>
    );
  }

  return (
    <DroppableArea droppableId="kpi-cards" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" isEditMode={isEditMode}>
      {enabledMetrics.map((metric, index) => {
        const config = getKPIConfig(metric.id);
        if (!config) return null;
        return (
          <DraggableKPICard key={metric.id} id={metric.id} index={index} isEditMode={isEditMode}>
            <KPICard 
              title={config.title}
              value={config.getValue()}
              change={config.getChange?.()}
              icon={config.icon}
              iconBg={config.iconBg}
            />
          </DraggableKPICard>
        );
      })}
    </DroppableArea>
  );
};
